import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatCurrency as formatCurrencyLib, CURRENCY_DATA } from '../lib/currency';
import { formatDate, getStatusColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, FileText, DollarSign, AlertCircle, CheckCircle2, Clock, Pencil, Trash2, MoreVertical } from 'lucide-react';

const Taxes = () => {
  const { company, canEdit } = useAuth();
  const currency = company?.currency || 'USD';
  const [loading, setLoading] = useState(true);
  const [taxTypes, setTaxTypes] = useState([]);
  const [taxEntries, setTaxEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [addEntryOpen, setAddEntryOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);

  const [newEntry, setNewEntry] = useState({
    tax_type_id: '',
    period: '',
    period_start: '',
    period_end: '',
    amount_due: '',
    currency: 'USD'
  });

  const [newPayment, setNewPayment] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_reference: '',
    payment_account_id: ''
  });

  // Helper to format currency using company default
  const formatCurrency = (amount) => {
    return formatCurrencyLib(amount || 0, currency);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [typesRes, entriesRes, accRes, summaryRes] = await Promise.all([
        api.get('/taxes/types'),
        api.get('/taxes/entries'),
        api.get('/accounts'),
        api.get('/taxes/summary')
      ]);
      setTaxTypes(typesRes.data);
      setTaxEntries(entriesRes.data);
      setAccounts(accRes.data.filter(a => ['cash', 'bank', 'mobile_money'].includes(a.account_subtype)));
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntry = async (e) => {
    e.preventDefault();
    try {
      if (editingEntry) {
        await api.put(`/taxes/entries/${editingEntry.id}`, {
          ...newEntry,
          amount_due: parseFloat(newEntry.amount_due)
        });
        toast.success('Tax entry updated');
      } else {
        await api.post('/taxes/entries', {
          ...newEntry,
          amount_due: parseFloat(newEntry.amount_due)
        });
        toast.success('Tax entry created');
      }
      setAddEntryOpen(false);
      setEditingEntry(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    }
  };

  const resetForm = () => {
    setNewEntry({
      tax_type_id: '',
      period: '',
      period_start: '',
      period_end: '',
      amount_due: '',
      currency: currency
    });
    setEditingEntry(null);
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setNewEntry({
      tax_type_id: entry.tax_type_id || '',
      period: entry.period || '',
      period_start: entry.period_start || '',
      period_end: entry.period_end || '',
      amount_due: entry.amount_due?.toString() || '',
      currency: entry.currency || 'USD'
    });
    setAddEntryOpen(true);
  };

  const handleDeleteEntry = async (entryId) => {
    if (!confirm('Are you sure you want to delete this tax entry?')) return;
    try {
      await api.delete(`/taxes/entries/${entryId}`);
      toast.success('Tax entry deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/taxes/entries/${selectedEntry.id}/payments`, {
        ...newPayment,
        amount: parseFloat(newPayment.amount)
      });
      toast.success('Payment recorded');
      setPaymentOpen(false);
      setSelectedEntry(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const openPaymentDialog = async (entry) => {
    try {
      const res = await api.get(`/taxes/entries/${entry.id}`);
      setSelectedEntry(res.data);
      setNewPayment({
        payment_date: new Date().toISOString().split('T')[0],
        amount: (parseFloat(entry.amount_due) - parseFloat(entry.amount_paid)).toFixed(2),
        payment_reference: '',
        payment_account_id: accounts[0]?.id || ''
      });
      setPaymentOpen(true);
    } catch (error) {
      toast.error('Failed to load entry details');
    }
  };

  // Generate period when tax type is selected
  const handleTaxTypeChange = (typeId) => {
    const type = taxTypes.find(t => t.id === typeId);
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    let period, start, end;
    if (type?.frequency === 'monthly') {
      period = `${year}-${String(month + 1).padStart(2, '0')}`;
      start = new Date(year, month, 1).toISOString().split('T')[0];
      end = new Date(year, month + 1, 0).toISOString().split('T')[0];
    } else if (type?.frequency === 'quarterly') {
      const quarter = Math.floor(month / 3);
      period = `${year}-Q${quarter + 1}`;
      start = new Date(year, quarter * 3, 1).toISOString().split('T')[0];
      end = new Date(year, quarter * 3 + 3, 0).toISOString().split('T')[0];
    } else {
      period = `${year}`;
      start = `${year}-01-01`;
      end = `${year}-12-31`;
    }
    
    setNewEntry({
      ...newEntry,
      tax_type_id: typeId,
      period,
      period_start: start,
      period_end: end
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle2 size={16} className="text-green-600" />;
      case 'overdue':
        return <AlertCircle size={16} className="text-red-600" />;
      default:
        return <Clock size={16} className="text-amber-600" />;
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            Taxes
          </h1>
          <p className="text-[hsl(var(--muted-ink))] mt-1">
            Track tax obligations and payments
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={() => setAddEntryOpen(true)}
            className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
            data-testid="add-tax-entry-button"
          >
            <Plus size={18} className="mr-2" />
            Add Tax Due
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[hsl(var(--muted-ink))] text-sm mb-1">
              <DollarSign size={14} />
              Total Due
            </div>
            <p className="text-2xl font-semibold numbered">
              {loading ? '-' : formatCurrency(summary?.total_due || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
              <CheckCircle2 size={14} />
              Total Paid
            </div>
            <p className="text-2xl font-semibold numbered text-green-600">
              {loading ? '-' : formatCurrency(summary?.total_paid || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-600 text-sm mb-1">
              <AlertCircle size={14} />
              Outstanding
            </div>
            <p className="text-2xl font-semibold numbered text-red-600">
              {loading ? '-' : formatCurrency(summary?.outstanding || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tax Entries */}
      <Card>
        <CardHeader>
          <CardTitle>Tax Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : taxEntries.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="font-medium mb-1">No tax entries yet</h3>
              <p className="text-sm text-[hsl(var(--muted-ink))]">Add your first tax obligation to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {taxEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    {getStatusIcon(entry.status)}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{entry.tax_type?.name || 'Tax'}</span>
                        <Badge className={getStatusColor(entry.status)} data-testid="tax-status-badge">
                          {entry.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-[hsl(var(--muted-ink))]">
                        Period: {entry.period}
                        {entry.due_date && ` • Due: ${formatDate(entry.due_date)}`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-[hsl(var(--muted-ink))]">Due</p>
                      <p className="font-semibold numbered">{formatCurrency(entry.amount_due)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-[hsl(var(--muted-ink))]">Paid</p>
                      <p className="font-semibold numbered text-green-600">{formatCurrency(entry.amount_paid)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {entry.status !== 'paid' && (
                        <Button
                          size="sm"
                          onClick={() => openPaymentDialog(entry)}
                          className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
                          disabled={!canEdit}
                        >
                          Pay
                        </Button>
                      )}
                      {canEdit && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`tax-menu-${entry.id}`}>
                              <MoreVertical size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditEntry(entry)} data-testid={`edit-tax-${entry.id}`}>
                              <Pencil size={14} className="mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteEntry(entry.id)} 
                              className="text-red-600"
                              data-testid={`delete-tax-${entry.id}`}
                            >
                              <Trash2 size={14} className="mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Entry Dialog */}
      <Dialog open={addEntryOpen} onOpenChange={(open) => { setAddEntryOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Tax Entry' : 'Add Tax Due'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEntry} className="space-y-4">
            <div>
              <Label>Tax Type</Label>
              <Select
                value={newEntry.tax_type_id}
                onValueChange={handleTaxTypeChange}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select tax type" />
                </SelectTrigger>
                <SelectContent>
                  {taxTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name} ({type.frequency})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Period</Label>
              <Input
                value={newEntry.period}
                onChange={(e) => setNewEntry({ ...newEntry, period: e.target.value })}
                placeholder="2024-03 or 2024-Q1"
                required
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start</Label>
                <Input
                  type="date"
                  value={newEntry.period_start}
                  onChange={(e) => setNewEntry({ ...newEntry, period_start: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Period End</Label>
                <Input
                  type="date"
                  value={newEntry.period_end}
                  onChange={(e) => setNewEntry({ ...newEntry, period_end: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount Due</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newEntry.amount_due}
                  onChange={(e) => setNewEntry({ ...newEntry, amount_due: e.target.value })}
                  placeholder="0.00"
                  required
                  className="mt-1 text-lg"
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select
                  value={newEntry.currency}
                  onValueChange={(value) => setNewEntry({ ...newEntry, currency: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="tax-currency-select">
                    <SelectValue placeholder="Currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CURRENCY_DATA).map(([code, data]) => (
                      <SelectItem key={code} value={code}>
                        {data.symbol} {code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => { setAddEntryOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white">
                {editingEntry ? 'Update Entry' : 'Add Entry'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Record Tax Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            {selectedEntry && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedEntry.tax_type?.name}</p>
                <p className="text-sm text-[hsl(var(--muted-ink))]">Period: {selectedEntry.period}</p>
                <p className="text-sm text-[hsl(var(--muted-ink))]">
                  Outstanding: {formatCurrency(parseFloat(selectedEntry.amount_due) - parseFloat(selectedEntry.amount_paid))}
                </p>
              </div>
            )}

            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={newPayment.payment_date}
                onChange={(e) => setNewPayment({ ...newPayment, payment_date: e.target.value })}
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                step="0.01"
                value={newPayment.amount}
                onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                required
                className="mt-1 text-lg"
              />
            </div>

            <div>
              <Label>Payment Account</Label>
              <Select
                value={newPayment.payment_account_id}
                onValueChange={(value) => setNewPayment({ ...newPayment, payment_account_id: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Reference (optional)</Label>
              <Input
                value={newPayment.payment_reference}
                onChange={(e) => setNewPayment({ ...newPayment, payment_reference: e.target.value })}
                placeholder="Receipt number, etc."
                className="mt-1"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setPaymentOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white">
                Record Payment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Taxes;
